import * as React from "react";
import { observer } from "mobx-react-lite";
import { Box, ButtonPrimary, ButtonSecondary, Stack, Tag, useScreenSize } from "@telefonica/mistica";
import { strings } from "../../localization";
import { formatAmount } from "../../utils/formatters";
import { SimulationValueCard } from "../SimulationValueCard";
import type { PartialAnticipationShelf } from "../../shelves";
import { CollapsibleContentUp } from "../CollapsibleContentUp";
import { AnticipationDetails } from "../AnticipationDetails";
import { useAnalytics } from "../../hooks/useAnalytics";
import { MIN_VALUE_ANTICIPATION } from "../../constants";

interface IProps {
	partialAnticipationShelf: PartialAnticipationShelf;
	isLoading: boolean;
	onEditFGTSValue(): void;
	onConfirmFGTSValue(): void;
}

export const PartialAnticipationResume: React.FC<IProps> = observer((props) => {
	const { partialAnticipationShelf, isLoading, onEditFGTSValue, onConfirmFGTSValue } = props;
	const partialSimulationStrings = strings.components.anticipationSimulation;
	const { isDesktopOrBigger } = useScreenSize();
	const { addAnalyticEvent } = useAnalytics();

	const handleCollapsibleAnalytic = (): void => {
		addAnalyticEvent({
			component: "body",
			action: "click.button",
			description: partialSimulationStrings.collapsibleTitle,
			step: "fgts:simulador-quente:resultado:parcial",
			value: partialData.value?.disbursedIssueAmount ,
		}, {applySanitize: true});
	};

	const partialData = partialAnticipationShelf.partialSimulationData;
	const minValueAnticipation = partialData.value !== null && partialData.value.disbursedIssueAmount >= MIN_VALUE_ANTICIPATION;

	return (
		<Stack space={24}>
			<Stack space={16}>
				<Box paddingTop={24} className={`${isDesktopOrBigger ? "center-aligned" : "children-full-width text-align-center"}`}>
					<Tag type="active">{`${partialSimulationStrings.maxFGTSValueAvailable} ${formatAmount(
						partialAnticipationShelf.maxFGTSValueAvailable.value,
					)}`}</Tag>
				</Box>
				<SimulationValueCard
					title={partialSimulationStrings.totalReceivable}
					value={partialData.value?.disbursedIssueAmount || 0}
					helperText={minValueAnticipation
						? partialSimulationStrings.yearsOfAnticipation(partialData.value?.yearsInAdvance || 0)
						: partialSimulationStrings.error
					}
				/>
			</Stack>
			<Stack space={16}>
				<ButtonPrimary
					disabled={isLoading || !minValueAnticipation}
					showSpinner={isLoading}
					onPress={onConfirmFGTSValue}
					className="button-full-width"
				>
					{strings.components.buttons.anticipateFGTS}
				</ButtonPrimary>
				<ButtonSecondary
					disabled={isLoading}
					showSpinner={isLoading}
					onPress={onEditFGTSValue}
					className="button-full-width"
				>
					{strings.components.buttons.changeValue}
				</ButtonSecondary>
			</Stack>
			<CollapsibleContentUp title={partialSimulationStrings.collapsibleTitle} analytic={handleCollapsibleAnalytic}>
				<AnticipationDetails
					blockedValue={partialData.value?.totalAmount || 0}
					contractFees={partialData.value?.totalPreFixedAmount || 0}
					IOF={partialData.value?.iofAmount || 0}
					CET={partialData.value?.annualCet || 0}
					yearsFees={partialData.value?.annualRate || 0}
					yearsAdvance={partialData.value?.yearsInAdvance || 0}
					tacAmount={partialData.value?.tacAmount || 0}
				/>
			</CollapsibleContentUp>
		</Stack>
	);
});
